import { lazy } from 'solid-js';
import type { RouteDefinition } from '@solidjs/router';

// One lazy component per page, shared by every path that renders it. A page
// reachable under several paths (an album with or without a lightbox photo,
// secret or collection) is a single route with a path array, so moving between
// those paths keeps the same component instead of swapping in a new one.
const Home = lazy(() => import('./pages/Home'));
const Events = lazy(() => import('./pages/Events'));
const Portfolio = lazy(() => import('./pages/Portfolio'));
const Photoshoots = lazy(() => import('./pages/Photoshoots'));
const PhotoshootAlbums = lazy(() => import('./pages/PhotoshootAlbums'));
const Concerts = lazy(() => import('./pages/Concerts'));
const Contact = lazy(() => import('./pages/Contact'));
const Login = lazy(() => import('./pages/Login'));
const Profile = lazy(() => import('./pages/Profile'));
const Liked = lazy(() => import('./pages/Liked'));
const TagAlbums = lazy(() => import('./pages/TagAlbums'));
const Collection = lazy(() => import('./pages/Collection'));
const AlbumDetail = lazy(() => import('./pages/AlbumDetail'));
const Year = lazy(() => import('./pages/Year'));
const Drive = lazy(() => import('./pages/Drive'));
const DriveFolder = lazy(() => import('./pages/DriveFolder'));
const DriveLiked = lazy(() => import('./pages/DriveLiked'));
const NotFound = lazy(() => import('./pages/NotFound'));

const numeric = /^\d+$/;

const mainRoutes: RouteDefinition[] = [
    { path: '/', component: Home },
    { path: '/upcoming-events', component: Events },
    { path: '/portfolio', component: Portfolio },
    { path: '/photoshoots', component: Photoshoots },
    { path: '/photoshoots/albums', component: PhotoshootAlbums },
    { path: '/concerts', component: Concerts },
    { path: '/contact', component: Contact },
    { path: '/login', component: Login },
    { path: '/profile', component: Profile },
    { path: ['/liked-photos', '/liked-photos/:photo'], component: Liked },
    { path: '/tag/:tag', component: TagAlbums },
    { path: ['/collection/:id', '/collection/:id/:secret'], component: Collection },
    {
        path: [
            '/album/tag/:tag/album/:id',
            '/album/collection/:collection/album/:id',
            '/album/collection/:collection/album/:id/:secret',
            '/album/collection/:collection/album/:id/photo/:photo',
            '/album/collection/:collection/album/:id/:secret/photo/:photo',
        ],
        component: AlbumDetail,
    },
    {
        path: ['/album/:id', '/album/:id/:secret', '/album/:id/photo/:photo', '/album/:id/:secret/photo/:photo'],
        matchFilters: { id: numeric, photo: numeric },
        component: AlbumDetail,
    },
    { path: '/:year', matchFilters: { year: /^\d{4}$/ }, component: Year },
    { path: '*404', component: NotFound },
];

const driveRoutes: RouteDefinition[] = [
    { path: ['/', '/drive'], component: Drive },
    { path: ['/p/:folder', '/p/:folder/:photoId'], component: DriveFolder },
    { path: ['/liked-photos', '/liked-photos/:photoId'], component: DriveLiked },
    // Account pages are served on both hosts (SecurityController).
    { path: '/login', component: Login },
    { path: '/profile', component: Profile },
    { path: '*404', component: NotFound },
];

/**
 * Picked at runtime in `AppRoot` based on `bootstrap.site.drive`. The two
 * subdomains share a single Vite bundle but use distinct route tables.
 */
export const routesFor = (isDrive: boolean): RouteDefinition[] => (isDrive ? driveRoutes : mainRoutes);
